import { getAreaSubstrateDimension } from '../../../util/calculate/get-areaSubstrate-dimension'
import type { OptionsDocument, QuoteDocument, QuoteOptions } from 'paintscout'
import type { QuoteItemSection, RenderableItemSubstrate } from '../../../..'
import { formatCurrency } from '../../../../util/format-currency'
import { getObjectLabels } from '../../../../util/get-object-labels'

/**
 * @description Build label for renderableSubItem
 * @returns Label for item <string>
 */
export const getItemLabel = ({
  item,
  quoteOptions,
  options,
  section,
  totalPrice,
  quote,
  hideDimensions
}: {
  item: RenderableItemSubstrate
  options: OptionsDocument
  quoteOptions: QuoteOptions
  section: QuoteItemSection
  quote: QuoteDocument
  totalPrice: number
  hideDimensions?: boolean
}): string => {
  // Construct label elements to combine
  const labelElements = []

  if (item.rateType === 'quantity' && !item.hideQuantity) {
    labelElements.push(item.quantity)
  }

  const inlineAreaOptions = section === 'options' || section === 'pending' || section === 'additional'
  if (!quoteOptions.showDetailedBreakdown && !quoteOptions.stackSubstrates && item.showPrice && inlineAreaOptions) {
    labelElements.push(formatCurrency({ options, value: totalPrice }))
  }
  if (quoteOptions.stackSubstrates && item.showPrice) {
    labelElements.push(formatCurrency({ options, value: totalPrice }))
  }

  if (item.showCoats && item.useCoats) {
    labelElements.push(`${item.coats} Coat${item.coats === 1 ? '' : 's'}`)
  }

  if (item.showPrep && item.prep) {
    labelElements.push(`${item.prep} hr${item.prep === 1 ? '' : 's'} prep`)
  }

  if (item.showDimensions && !hideDimensions) {
    const rateType = item.rateType
    const isLnft = rateType === 'lnft'
    const itemArea = Object.values(quote.areas).find((area) => area.key === item.areaKey)
    const isRoom = itemArea?.area_type?.value === 'room'
    const objectLabels = getObjectLabels({ options })

    const hasCustomSubstrateDimensions =
      item.length.useCustom || item.width.useCustom || item.height.useCustom || item.dimensionOverride != null

    let dimensionValue: number
    if (hasCustomSubstrateDimensions) {
      const length = item?.length?.useCustom ? item?.length?.custom ?? 0 : item?.length?.default ?? 0
      const width = item?.width?.useCustom ? item?.width?.custom ?? 0 : item?.width?.default ?? 0
      const height = item?.height?.useCustom ? item?.height?.custom ?? 0 : item?.height?.default ?? 0
      dimensionValue =
        item.dimensionOverride != null
          ? item.dimensionOverride
          : getAreaSubstrateDimension({ length, width, height, isRoom, rateType })
    } else {
      // Fall back to area-level dimensions, same as getDimension in AreaSubstrates.tsx
      const areaDims = itemArea?.dimensions
      switch (rateType) {
        case 'lnft':
          dimensionValue = Number(areaDims?.lnft) || 0
          break
        case 'sqftWalls':
        case 'sqft_walls':
          dimensionValue = isRoom ? Number(areaDims?.sqft_walls) || 0 : Number(areaDims?.sqft) || 0
          break
        case 'sqftFloor':
        case 'sqft_floor':
          dimensionValue = isRoom ? Number(areaDims?.sqft_floor) || 0 : Number(areaDims?.sqft) || 0
          break
        case 'sqftCeiling':
        case 'sqft_ceiling':
          dimensionValue = isRoom ? Number(areaDims?.sqft_ceiling) || 0 : Number(areaDims?.sqft) || 0
          break
        default:
          dimensionValue = Number(areaDims?.sqft) || 0
          break
      }
    }

    const dimension = `${dimensionValue} ${isLnft ? objectLabels.unit.abbreviation.linear : objectLabels.unit.abbreviation.square}`
    labelElements.push(dimension)
  }

  // Include product details if present
  if (item.showProduct && item.products.length) {
    const { products } = item
    for (const product of products) {
      const productString = [product.label, product.color].filter((item) => item).join(' - ')
      if (productString) {
        labelElements.push(productString)
      }
    }
  }

  // Combine elements into full description
  const labelString = labelElements.length ? ` (${labelElements.join(', ')})` : ''

  return labelString
}
